import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CalendarClock, Loader2, ArrowRight } from "lucide-react";
import { isoToBusinessInput, businessInputToIso, formatBusinessDateTime } from "@/lib/businessTz";

// datetime-local always represents America/Chicago wall-clock, independent
// of the browser timezone. Prevents technicians in other timezones from
// writing drifted instants into the owner's calendar.
const toLocalInputValue = (iso) => isoToBusinessInput(iso);
const localInputToIso = (v) => businessInputToIso(v);

const fmtLocal = (iso) => (iso ? formatBusinessDateTime(iso) : "TBD");

/**
 * RescheduleAppointmentDialog
 *
 * Two modes:
 *   1. Quick-confirm (drag-drop from calendar):
 *      Pass `proposedStartIso` to skip the picker and just show From/To + Confirm/Cancel.
 *   2. Editor (from Job or Estimate page):
 *      Omit `proposedStartIso` to render date/time inputs. Duration preserved from
 *      the current appointment when no explicit end is provided.
 *
 * Persistence: PATCH /api/jobs/{jobId}. On success, writes a `job_audit_log`
 * entry client-side via /api/jobs/{jobId}/audit (best-effort, non-blocking).
 */
export default function RescheduleAppointmentDialog({
  open, onOpenChange, job, proposedStartIso, onRescheduled,
}) {
  const [busy, setBusy] = useState(false);
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open || !job) return;
    setBusy(false);
    setNote("");
    if (proposedStartIso) {
      setNewStart(proposedStartIso);
      // Preserve duration
      if (job.scheduled_start && job.scheduled_end) {
        const durMs = new Date(job.scheduled_end).getTime() - new Date(job.scheduled_start).getTime();
        setNewEnd(new Date(new Date(proposedStartIso).getTime() + durMs).toISOString());
      } else {
        setNewEnd("");
      }
    } else {
      setNewStart(job.scheduled_start || "");
      setNewEnd(job.scheduled_end || "");
    }
  }, [open, job?.id, proposedStartIso]);

  const isQuick = !!proposedStartIso;
  const currentStart = job?.scheduled_start;
  const currentEnd = job?.scheduled_end;

  const submit = async () => {
    if (!job?.id || !newStart) return;
    setBusy(true);
    try {
      const payload = {
        scheduled_start: new Date(newStart).toISOString(),
        scheduled_end: newEnd ? new Date(newEnd).toISOString() : undefined,
      };
      await api.patch(`/jobs/${job.id}`, payload);
      // Best-effort activity log — silent on failure so the reschedule stays authoritative
      try {
        await api.post(`/jobs/${job.id}/audit`, {
          action: "rescheduled",
          detail: {
            from: currentStart,
            to: payload.scheduled_start,
            note: note || null,
            source: isQuick ? "calendar_drag_drop" : "reschedule_dialog",
          },
        });
      } catch { /* audit endpoint may not exist yet; job PATCH already recorded */ }
      toast.success("Appointment rescheduled");
      onRescheduled?.(payload);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reschedule");
    } finally { setBusy(false); }
  };

  if (!open || !job) return null;

  if (isQuick) {
    // Compact confirmation UX for calendar drops
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent data-testid="reschedule-quick-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-[#FF5A00]"/> Reschedule appointment?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <div className="mt-2 space-y-2 text-sm">
                <div className="font-medium text-zinc-800" data-testid="reschedule-quick-job-title">{job.title}</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="rounded border border-zinc-200 bg-zinc-50 px-3 py-2 flex-1 min-w-[180px]">
                    <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">From</div>
                    <div className="font-medium" data-testid="reschedule-quick-from">{fmtLocal(currentStart)}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-400"/>
                  <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 flex-1 min-w-[180px]">
                    <div className="text-[10px] uppercase font-mono-accent tracking-widest text-emerald-700">To</div>
                    <div className="font-medium text-emerald-800" data-testid="reschedule-quick-to">{fmtLocal(newStart)}</div>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reschedule-quick-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submit} disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="reschedule-quick-confirm-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Saving…</> : "Confirm reschedule"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Editor UX for Job/Estimate pages
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reschedule-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#FF5A00]"/> Reschedule appointment
          </DialogTitle>
          <DialogDescription>{job.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded border border-zinc-200 bg-zinc-50 p-3 text-sm">
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">Currently scheduled</div>
            <div className="font-medium" data-testid="reschedule-editor-current">{fmtLocal(currentStart)}</div>
            {currentEnd && <div className="text-xs text-zinc-500">until {fmtLocal(currentEnd)}</div>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase font-mono-accent">New start</Label>
              <Input type="datetime-local" value={toLocalInputValue(newStart)}
                     onChange={(e)=>setNewStart(localInputToIso(e.target.value))}
                     data-testid="reschedule-editor-start"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono-accent">New end (optional)</Label>
              <Input type="datetime-local" value={toLocalInputValue(newEnd)}
                     onChange={(e)=>setNewEnd(localInputToIso(e.target.value))}
                     data-testid="reschedule-editor-end"/>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono-accent">Internal note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e)=>setNote(e.target.value)}
                      placeholder="e.g. rain delay, customer request"
                      data-testid="reschedule-editor-note"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !newStart}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="reschedule-editor-save">
            {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Saving…</> : "Confirm reschedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
